import React, {useEffect, useState} from 'react';
import {Alert, Spinner} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';
import {useFetchUserSolveHeatmapQuery} from '@api/users';
import {useTheme} from '@components/theme/useTheme';
import {HeatmapData} from '@types';

const getHeatmapColors = (
    isDark: boolean
): Record<number, React.CSSProperties> => {
    if (isDark) {
        return {
            0: {backgroundColor: '#262c36'},
            1: {backgroundColor: '#0e4429'},
            2: {backgroundColor: '#006d32'},
            3: {backgroundColor: '#26a641'},
            4: {backgroundColor: '#39d353'},
        };
    }
    return {
        0: {backgroundColor: '#ebedf0'},
        1: {backgroundColor: '#9be9a8'},
        2: {backgroundColor: '#40c463'},
        3: {backgroundColor: '#30a14e'},
        4: {backgroundColor: '#216e39'},
    };
};

export const SolveHeatmap = ({
    userId,
    dateFrom,
    dateTo,
    cellSize = 12,
    fitWidth = false,
    labelSize,
    showMonthLabels = true,
    showDayLabels = true,
    showLegend = true,
    autoScrollToEnd = false,
    scrollContainerRef,
}: {
    userId: number;
    dateFrom?: string;
    dateTo?: string;
    cellSize?: number;
    fitWidth?: boolean;
    labelSize?: number;
    showMonthLabels?: boolean;
    showDayLabels?: boolean;
    showLegend?: boolean;
    autoScrollToEnd?: boolean;
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
    const {t} = useTranslation();
    const [fittedCellSize, setFittedCellSize] = useState(cellSize);

    const calculatedLabelSize = labelSize ?? Math.max(8, cellSize);

    const {
        data: heatmapData,
        isLoading: loading,
        error,
    } = useFetchUserSolveHeatmapQuery({
        userId,
        dateFrom,
        dateTo,
    });

    useEffect(() => {
        const container = scrollContainerRef?.current;
        if (!fitWidth || !container || !heatmapData) return;
        const from = new Date(heatmapData.dateFrom);
        const days =
            Math.round(
                (new Date(heatmapData.dateTo).getTime() - from.getTime()) /
                    86400000
            ) + 1;
        const weeks = Math.ceil((days + from.getDay()) / 7);
        const resize = () => {
            const content = container.querySelector('.solve-heatmap > div');
            const styles = content ? getComputedStyle(content) : null;
            const padding = styles
                ? parseFloat(styles.paddingLeft) +
                  parseFloat(styles.paddingRight)
                : 16;
            let size = 7;
            for (let candidate = 20; candidate >= 7; candidate -= 0.125) {
                const labels = showDayLabels
                    ? Math.max(24, candidate * 3) +
                      Math.max(2, Math.floor(candidate / 4))
                    : 0;
                if (
                    weeks * (candidate + 2) + labels + padding + 2 <=
                    container.clientWidth
                ) {
                    size = candidate;
                    break;
                }
            }
            setFittedCellSize(size);
            // Also follow the latest dates when the viewport narrows without
            // changing the minimum cell size.
            if (autoScrollToEnd) container.scrollLeft = container.scrollWidth;
        };
        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(container);
        return () => observer.disconnect();
    }, [
        fitWidth,
        heatmapData,
        scrollContainerRef,
        showDayLabels,
        autoScrollToEnd,
    ]);

    useEffect(() => {
        if (autoScrollToEnd && heatmapData && scrollContainerRef?.current) {
            const scrollToRight = () => {
                const element = scrollContainerRef.current;
                if (element && element.scrollWidth > element.clientWidth) {
                    element.scrollLeft = element.scrollWidth;
                }
            };

            const timer = setTimeout(scrollToRight, 0);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [autoScrollToEnd, heatmapData, scrollContainerRef, fittedCellSize]);

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">
                        {t('common.loading')}
                    </span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        if ('status' in error && error.status === 403) {
            return null;
        }

        const errorMessage =
            'status' in error
                ? `Error ${error.status}: ${JSON.stringify(error.data)}`
                : error.message || 'Unknown error';

        return (
            <Alert variant="danger">
                {t('heatmap.error')}: {errorMessage}
            </Alert>
        );
    }

    if (!heatmapData) {
        return null;
    }

    return (
        <div className="solve-heatmap">
            <div
                className="flex flex-col gap-2 p-2 bg-body"
                style={{alignItems: 'flex-start'}}>
                <HeatmapGrid
                    data={heatmapData.data}
                    dateFrom={heatmapData.dateFrom}
                    dateTo={heatmapData.dateTo}
                    cellSize={fitWidth ? fittedCellSize : cellSize}
                    labelSize={calculatedLabelSize}
                    showMonthLabels={showMonthLabels}
                    showDayLabels={showDayLabels}
                    showLegend={showLegend}
                />
            </div>
        </div>
    );
};

type HeatmapGridProps = {
    data: HeatmapData[];
    dateFrom: string;
    dateTo: string;
    cellSize?: number;
    labelSize?: number;
    showMonthLabels?: boolean;
    showDayLabels?: boolean;
    showLegend?: boolean;
};

const HeatmapGrid = ({
    data,
    dateFrom,
    dateTo,
    cellSize = 12,
    labelSize = 12,
    showMonthLabels = true,
    showDayLabels = true,
    showLegend = true,
}: HeatmapGridProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isDark = theme === 'dark';

    const dataMap = new Map<string, HeatmapData>();
    data.forEach(item => dataMap.set(item.date, item));

    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);
    const today = new Date();
    const todayString = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, '0'),
        String(today.getDate()).padStart(2, '0'),
    ].join('-');

    const weeks: (HeatmapData | null)[][] = [];
    let currentWeek: (HeatmapData | null)[] = new Array(7).fill(null);

    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        const dateString = currentDate.toISOString().split('T')[0];
        const dayOfWeek = currentDate.getUTCDay();

        const dayData =
            dateString > todayString
                ? null
                : dataMap.get(dateString) || {
                      date: dateString,
                      count: 0,
                      level: 0,
                  };

        currentWeek[dayOfWeek] = dayData;

        if (dayOfWeek === 6 || currentDate.getTime() === endDate.getTime()) {
            weeks.push([...currentWeek]);
            currentWeek = new Array(7).fill(null);
        }

        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    const getTooltipText = (dayData: HeatmapData | null): string => {
        if (!dayData) {
            return '';
        }

        const date = new Date(`${dayData.date}T00:00:00`);
        const formattedDate = date.toLocaleDateString();

        if (dayData.count === 0) {
            return t('heatmap.tooltip.no_solves', {date: formattedDate});
        } else if (dayData.count === 1) {
            return t('heatmap.tooltip.one_solve', {date: formattedDate});
        } else {
            return t('heatmap.tooltip.multiple_solves', {
                count: dayData.count,
                date: formattedDate,
            });
        }
    };

    const generateMonthLabels = (weeks: (HeatmapData | null)[][]) => {
        const monthLabels: Array<{
            label: string;
            weekIndex: number;
            span: number;
        }> = [];
        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ];

        let currentMonth = -1;
        let monthStartWeek = 0;

        weeks.forEach((week, weekIndex) => {
            const firstDay = week.find(day => day !== null);
            if (firstDay) {
                const date = new Date(firstDay.date);
                const monthIndex = date.getUTCMonth();

                if (monthIndex !== currentMonth) {
                    if (currentMonth !== -1 && monthLabels.length > 0) {
                        monthLabels[monthLabels.length - 1].span =
                            weekIndex - monthStartWeek;
                    }

                    currentMonth = monthIndex;
                    monthStartWeek = weekIndex;
                    monthLabels.push({
                        label: monthNames[monthIndex],
                        weekIndex: weekIndex,
                        span: 1,
                    });
                }
            }
        });

        if (monthLabels.length > 0) {
            monthLabels[monthLabels.length - 1].span =
                weeks.length - monthStartWeek;
        }

        return monthLabels;
    };

    const monthLabels = generateMonthLabels(weeks);

    const verticalGap = Math.max(2, Math.floor(cellSize / 4));
    const horizontalGap = Math.max(2, Math.floor(cellSize / 4));
    const labelWidth = Math.max(24, cellSize * 3);

    return (
        <div
            className="flex flex-col text-xs"
            style={{gap: `${verticalGap}px`}}>
            {showMonthLabels && (
                <div
                    className="flex items-start"
                    style={{minWidth: 'max-content'}}>
                    <div
                        className="flex-shrink-0"
                        style={{
                            width: showDayLabels
                                ? `${labelWidth + horizontalGap}px`
                                : '0px',
                        }}></div>
                    <div className="flex">
                        {monthLabels.map((month, index) => {
                            const width =
                                month.span * cellSize + month.span * 2;

                            return (
                                <div
                                    key={index}
                                    className="text-left text-neutral-500 dark:text-neutral-400"
                                    style={{
                                        width: `${width}px`,
                                        minWidth: `${width}px`,
                                        fontSize: `${labelSize}px`,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        visibility:
                                            width < labelSize * 2.5
                                                ? 'hidden'
                                                : undefined,
                                    }}>
                                    {month.label}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div
                className="flex"
                style={{
                    gap: `${horizontalGap}px`,
                    minWidth: 'max-content',
                }}>
                {showDayLabels && (
                    <div
                        className="flex flex-col flex-shrink-0 gap-0.5"
                        style={{width: `${labelWidth}px`}}>
                        <span
                            className="flex items-center text-muted opacity-0"
                            style={{
                                fontSize: `${labelSize}px`,
                                height: `${cellSize}px`,
                            }}>
                            {t('weekdays.short.sun')}
                        </span>
                        <span
                            className="flex items-center text-muted"
                            style={{
                                fontSize: `${labelSize}px`,
                                height: `${cellSize}px`,
                            }}>
                            {t('weekdays.short.mon')}
                        </span>
                        <span
                            className="flex items-center text-muted opacity-0"
                            style={{
                                fontSize: `${labelSize}px`,
                                height: `${cellSize}px`,
                            }}>
                            {t('weekdays.short.tue')}
                        </span>
                        <span
                            className="flex items-center text-muted"
                            style={{
                                fontSize: `${labelSize}px`,
                                height: `${cellSize}px`,
                            }}>
                            {t('weekdays.short.wed')}
                        </span>
                        <span
                            className="flex items-center text-muted opacity-0"
                            style={{
                                fontSize: `${labelSize}px`,
                                height: `${cellSize}px`,
                            }}>
                            {t('weekdays.short.thu')}
                        </span>
                        <span
                            className="flex items-center text-muted"
                            style={{
                                fontSize: `${labelSize}px`,
                                height: `${cellSize}px`,
                            }}>
                            {t('weekdays.short.fri')}
                        </span>
                        <span
                            className="flex items-center text-muted opacity-0"
                            style={{
                                fontSize: `${labelSize}px`,
                                height: `${cellSize}px`,
                            }}>
                            {t('weekdays.short.sat')}
                        </span>
                    </div>
                )}

                <div className="flex gap-0.5 flex-nowrap">
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="flex flex-col gap-0.5">
                            {week.map((day, dayIndex) => {
                                const levelStyles = getHeatmapColors(isDark);

                                if (!day) {
                                    return (
                                        <div
                                            key={`${weekIndex}-${dayIndex}`}
                                            style={{
                                                width: `${cellSize}px`,
                                                height: `${cellSize}px`,
                                            }}
                                        />
                                    );
                                }

                                const level = day.level || 0;
                                const style =
                                    levelStyles[level] || levelStyles[0];

                                return (
                                    <div
                                        key={`${weekIndex}-${dayIndex}`}
                                        style={{
                                            width: `${cellSize}px`,
                                            height: `${cellSize}px`,
                                            borderRadius: '1px',
                                            cursor: 'default',
                                            ...style,
                                        }}
                                        title={getTooltipText(day)}
                                        data-count={day.count}
                                        data-date={day.date}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {showLegend && (
                <div className="flex justify-end mt-2">
                    <HeatmapLegend cellSize={cellSize} labelSize={labelSize} />
                </div>
            )}
        </div>
    );
};

const HeatmapLegend: React.FC<{
    cellSize?: number;
    labelSize?: number;
}> = ({cellSize = 12, labelSize = 12}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isDark = theme === 'dark';

    const legendStyles = getHeatmapColors(isDark);

    return (
        <div className="flex items-center gap-2 text-muted">
            <span style={{fontSize: `${labelSize}px`}}>
                {t('heatmap.legend.less')}
            </span>
            <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map(level => (
                    <div
                        key={level}
                        style={{
                            width: `${cellSize}px`,
                            height: `${cellSize}px`,
                            borderRadius: '1px',
                            ...legendStyles[level],
                        }}
                    />
                ))}
            </div>
            <span style={{fontSize: `${labelSize}px`}}>
                {t('heatmap.legend.more')}
            </span>
        </div>
    );
};
